/**
 * 📅 Actividades por Día Universal
 * 
 * Sistema de sugerencias de actividades basado en numerología.
 * Cada actividad tiene un "mejor día universal" (1-9) y puede tener
 * referencias a días personales, meses universales, etc. en su interpretación.
 * 
 * El sistema de "matches" prioriza:
 * 1. Triple Match: Día Universal + Día Personal + Mes Universal
 * 2. Doble Match: Cualquier combinación de dos coincidencias
 * 3. Match Simple: Solo coincide el día universal
 */

export const ACTIVITY_CATEGORIES = [
  {
    id: 'trabajo',
    name: 'Trabajo y Profesión',
    icon: '💼',
    color: 'var(--color-morado)',
    gradient: 'bg-morado'
  },
  {
    id: 'salud',
    name: 'Salud y Belleza',
    icon: '💆',
    color: '#059669',
    gradient: 'bg-emerald-600'
  },
  {
    id: 'amor',
    name: 'Amor, Familia y Placer',
    icon: '❤️',
    color: '#dc2626',
    gradient: 'bg-rose-600'
  },
  {
    id: 'vida',
    name: 'Vida Diaria',
    icon: '🏠',
    color: '#d97706',
    gradient: 'bg-amber-600'
  }
];

/**
 * Todas las actividades con su mejor día y metadatos extraídos
 * de la interpretación para el sistema de matches.
 * 
 * Campos:
 * - bestDay: Día Universal ideal (1-9)
 * - personalDays: Días personales mencionados en la interpretación
 * - universalMonths: Meses universales mencionados
 * - universalWeeks: Semanas universales mencionadas
 * - masterNumbers: Números maestros mencionados (11, 22)
 */
export const ACTIVITIES = [
  // ═══════════════════════════════════════════════════════════════
  // 💼 TRABAJO Y PROFESIÓN
  // ═══════════════════════════════════════════════════════════════
  {
    id: 'generar-ideas',
    category: 'trabajo',
    name: 'Generar ideas innovadoras',
    bestDay: 3,
    personalDays: [],
    universalMonths: [],
    universalWeeks: [3],
    masterNumbers: [],
    interpretation: 'El 3 despierta tu creatividad expresiva, ideal para brainstormings que fluyan con alegría. En 2026, úsalo en semanas 3 para innovar en proyectos estancados, atrayendo aliados inesperados.'
  },
  {
    id: 'hacer-presupuestos',
    category: 'trabajo',
    name: 'Hacer presupuestos y/o trámites',
    bestDay: 4,
    personalDays: [],
    universalMonths: [4],
    universalWeeks: [],
    masterNumbers: [],
    interpretation: 'Vibración de estructura y disciplina; construye bases sólidas. Durante meses 4, evita impulsos y enfócate en auditorías financieras para cerrar ciclos de deudas kármicas.'
  },
  {
    id: 'iniciar-proyecto',
    category: 'trabajo',
    name: 'Iniciar un proyecto o empresa',
    bestDay: 1,
    personalDays: [],
    universalMonths: [],
    universalWeeks: [1],
    masterNumbers: [],
    interpretation: 'Día de liderazgo e inicios audaces, como un rayo de independencia. Inicia en semanas 1 para manifestar visiones maestras, confiando en tu intuición.'
  },
  {
    id: 'venta-negociacion',
    category: 'trabajo',
    name: 'Largar una venta, contrato o negociación',
    bestDay: 8,
    personalDays: [],
    universalMonths: [8],
    universalWeeks: [],
    masterNumbers: [],
    interpretation: 'Potencia el poder material y el karma positivo. Úsalo en meses 8 para negociar con autoridad, recomendando meditación previa para atraer abundancia.'
  },
  {
    id: 'terminar-tareas',
    category: 'trabajo',
    name: 'Terminar tareas inconclusas',
    bestDay: 9,
    personalDays: [],
    universalMonths: [],
    universalWeeks: [9],
    masterNumbers: [],
    interpretation: 'Cierre culminante, liberador de energías atascadas. En semanas 9, finaliza con gratitud, abriendo espacio para objetivos humanitarios.'
  },
  {
    id: 'firmar-papeles',
    category: 'trabajo',
    name: 'Firmar papeles importantes',
    bestDay: 4,
    personalDays: [7],
    universalMonths: [],
    universalWeeks: [],
    masterNumbers: [],
    interpretation: 'Estabilidad y formalidad; evita errores. Combínalo con días personales 7 para introspección, asegurando decisiones asertivas.'
  },
  {
    id: 'contratar-personal',
    category: 'trabajo',
    name: 'Contratar personal',
    bestDay: 2,
    personalDays: [],
    universalMonths: [2],
    universalWeeks: [],
    masterNumbers: [],
    interpretation: 'Colaboración y armonía en equipo. En meses 2, busca vínculos sanos, recordando que las relaciones equilibradas alcanzan metas colectivas.'
  },
  {
    id: 'publicidad-promocion',
    category: 'trabajo',
    name: 'Hacer publicidad o promoción',
    bestDay: 3,
    personalDays: [],
    universalMonths: [],
    universalWeeks: [3],
    masterNumbers: [],
    interpretation: 'Expresión creativa y social; brilla con carisma. Úsalo en semanas 3 para promociones virales, alineando con tu vibración de renovación.'
  },
  {
    id: 'trabajar-equipo',
    category: 'trabajo',
    name: 'Trabajar en equipo / grupos',
    bestDay: 6,
    personalDays: [],
    universalMonths: [6],
    universalWeeks: [],
    masterNumbers: [],
    interpretation: 'Responsabilidad familiar y nutricia en lo profesional. Durante meses 6, fomenta lealtad, aconsejando límites para no sacrificar tu bienestar.'
  },
  {
    id: 'obtener-reconocimiento',
    category: 'trabajo',
    name: 'Obtener reconocimiento',
    bestDay: 8,
    personalDays: [],
    universalMonths: [],
    universalWeeks: [8],
    masterNumbers: [],
    interpretation: 'Éxito material y autoridad; reclama tu valor. En semanas 8, visualiza premios, atrayendo reconocimiento que impulse tus objetivos de liderazgo.'
  },
  {
    id: 'lograr-colaboracion',
    category: 'trabajo',
    name: 'Lograr colaboración de todos',
    bestDay: 2,
    personalDays: [],
    universalMonths: [],
    universalWeeks: [],
    masterNumbers: [11],
    interpretation: 'Diplomacia y unión; resuelve conflictos. Combínalo con días 11 para inspiración maestra.'
  },
  {
    id: 'visitar-ideas',
    category: 'trabajo',
    name: 'Revisar tus ideas o puntos de vista',
    bestDay: 7,
    personalDays: [],
    universalMonths: [7],
    universalWeeks: [],
    masterNumbers: [],
    interpretation: 'Introspección y análisis profundo. En meses 7, reflexiona solo para ganar claridad en decisiones estratégicas.'
  },
  {
    id: 'expresar-publico',
    category: 'trabajo',
    name: 'Expresar tus ideas en público',
    bestDay: 3,
    personalDays: [],
    universalMonths: [],
    universalWeeks: [3],
    masterNumbers: [],
    interpretation: 'Creatividad verbal; conquista audiencias. Úsalo en semanas 3 para conferencias que alineen con tu propósito.'
  },
  {
    id: 'cobrar-cuentas',
    category: 'trabajo',
    name: 'Cobrar cuentas atrasadas',
    bestDay: 8,
    personalDays: [],
    universalMonths: [8],
    universalWeeks: [],
    masterNumbers: [],
    interpretation: 'Poder financiero; cierra deudas con firmeza. En meses 8, actúa con integridad kármica.'
  },
  {
    id: 'activar-ideas',
    category: 'trabajo',
    name: 'Activar y poner en práctica tus ideas',
    bestDay: 5,
    personalDays: [],
    universalMonths: [],
    universalWeeks: [5],
    masterNumbers: [],
    interpretation: 'Cambio y aventura; rompe rutinas. Durante semanas 5, abraza lo inesperado para progresar.'
  },
  {
    id: 'dar-ordenes',
    category: 'trabajo',
    name: 'Dar órdenes efectivas',
    bestDay: 1,
    personalDays: [],
    universalMonths: [1],
    universalWeeks: [],
    masterNumbers: [],
    interpretation: 'Liderazgo asertivo; toma el mando. En días 1 de meses 1, inicia ciclos de autoridad.'
  },
  {
    id: 'brillar-demas',
    category: 'trabajo',
    name: 'Brillar ante los demás',
    bestDay: 3,
    personalDays: [],
    universalMonths: [],
    universalWeeks: [],
    masterNumbers: [22],
    interpretation: 'Expresión radiante; sé el centro. Combínalo con vibraciones 22 para construcciones maestras.'
  },
  {
    id: 'invertir-dinero',
    category: 'trabajo',
    name: 'Invertir tu dinero',
    bestDay: 8,
    personalDays: [],
    universalMonths: [],
    universalWeeks: [8],
    masterNumbers: [],
    interpretation: 'Ambición material; multiplica con sabiduría. En semanas 8, invierte en metas a largo plazo.'
  },
  {
    id: 'fuerza-liderazgo',
    category: 'trabajo',
    name: 'Fuerza y liderazgo frente a otros',
    bestDay: 1,
    personalDays: [],
    universalMonths: [1],
    universalWeeks: [],
    masterNumbers: [],
    interpretation: 'Independencia pura; lidera con coraje. Úsalo en meses 1 para decisiones transformadoras.'
  },
  {
    id: 'tramites-legales',
    category: 'trabajo',
    name: 'Realizar trámites legales',
    bestDay: 4,
    personalDays: [],
    universalMonths: [],
    universalWeeks: [4],
    masterNumbers: [],
    interpretation: 'Estructura legal; formaliza con paciencia. En semanas 4, evita prisas para estabilidad duradera.'
  },
  {
    id: 'comidas-negocios',
    category: 'trabajo',
    name: 'Comidas o cenas de negocios',
    bestDay: 2,
    personalDays: [],
    universalMonths: [],
    universalWeeks: [2],
    masterNumbers: [],
    interpretation: 'Colaboración diplomática y sensible; une con empatía. Durante semanas 2, negocia con equilibrio, aconsejando escucha intuitiva para atraer socios que respeten tu autoridad.'
  },
  {
    id: 'negociar-acuerdo',
    category: 'trabajo',
    name: 'Negociar para un acuerdo',
    bestDay: 2,
    personalDays: [],
    universalMonths: [2],
    universalWeeks: [],
    masterNumbers: [],
    interpretation: 'Diplomacia sensible y unión; resuelve con equilibrio mutuo. Úsalo en meses 2 para tratos, recomendando empatía para acuerdos justos que fortalezcan metas colectivas.'
  },
  {
    id: 'persuadir-personas',
    category: 'trabajo',
    name: 'Persuadir a otras personas',
    bestDay: 3,
    personalDays: [],
    universalMonths: [],
    universalWeeks: [3],
    masterNumbers: [],
    interpretation: 'Comunicación expresiva influyente; convence con encanto. Úsalo en semanas 3 para diálogos, recomendando autenticidad para decisiones que atraigan aliados.'
  },
  {
    id: 'vender-buen-precio',
    category: 'trabajo',
    name: 'Vender para obtener buen precio',
    bestDay: 8,
    personalDays: [],
    universalMonths: [8],
    universalWeeks: [],
    masterNumbers: [],
    interpretation: 'Ambición material poderosa; negocia con autoridad kármica. Durante meses 8, vende con integridad para abundancia que eleve tu liderazgo financiero.'
  },
  {
    id: 'viajes-negocios',
    category: 'trabajo',
    name: 'Viajes activos o de negocios',
    bestDay: 5,
    personalDays: [],
    universalMonths: [],
    universalWeeks: [5],
    masterNumbers: [],
    interpretation: 'Aventura práctica liberadora; mueve con flexibilidad. En semanas 5, desplázate productivamente, priorizando adaptabilidad para descubrimientos que impulsen metas independientes.'
  },

  // ═══════════════════════════════════════════════════════════════
  // 💆 SALUD Y BELLEZA
  // ═══════════════════════════════════════════════════════════════
  {
    id: 'empezar-ejercicio',
    category: 'salud',
    name: 'Empezar a hacer ejercicio',
    bestDay: 1,
    personalDays: [],
    universalMonths: [],
    universalWeeks: [1],
    masterNumbers: [],
    interpretation: 'Inicia con energía vital y determinación. En semanas 1, combina con tu año personal para vitalidad renovada, soltando sedentarismo kármico.'
  },
  {
    id: 'cambio-imagen',
    category: 'salud',
    name: 'Hacerte un cambio de imagen',
    bestDay: 5,
    personalDays: [],
    universalMonths: [5],
    universalWeeks: [],
    masterNumbers: [],
    interpretation: 'Transformación liberadora; atrévete al cambio. Durante meses 5, elige looks que reflejen tu esencia maestra.'
  },
  {
    id: 'spa-recargar',
    category: 'salud',
    name: 'Asistir a un SPA y cargar tus pilas',
    bestDay: 6,
    personalDays: [],
    universalMonths: [],
    universalWeeks: [6],
    masterNumbers: [],
    interpretation: 'Armonía y autocuidado; nutre tu cuerpo. En semanas 6, prioriza descanso para equilibrar energías.'
  },
  {
    id: 'empezar-dieta',
    category: 'salud',
    name: 'Empezar una dieta',
    bestDay: 4,
    personalDays: [9],
    universalMonths: [],
    universalWeeks: [],
    masterNumbers: [],
    interpretation: 'Disciplina estructurada; construye hábitos. Combínalo con días 9 para cierres saludables.'
  },
  {
    id: 'ordenar-limpiar',
    category: 'salud',
    name: 'Ordenar, clasificar, limpiar y reparar',
    bestDay: 4,
    personalDays: [],
    universalMonths: [4],
    universalWeeks: [],
    masterNumbers: [],
    interpretation: 'Orden interno; limpia energías estancadas. En meses 4, usa para detox emocional.'
  },
  {
    id: 'competiciones-deportivas',
    category: 'salud',
    name: 'Participar en competiciones deportivas',
    bestDay: 1,
    personalDays: [],
    universalMonths: [],
    universalWeeks: [1],
    masterNumbers: [],
    interpretation: 'Competencia y liderazgo; vence límites. Úsalo en semanas 1 para victorias personales.'
  },
  {
    id: 'cuidar-piel-masajes',
    category: 'salud',
    name: 'Cuidar de la piel y masajes',
    bestDay: 6,
    personalDays: [],
    universalMonths: [6],
    universalWeeks: [],
    masterNumbers: [],
    interpretation: 'Belleza armónica; mima tu templo. Durante meses 6, atrae paz interior.'
  },
  {
    id: 'dejar-fumar',
    category: 'salud',
    name: 'Dejar de fumar, romper patrones',
    bestDay: 9,
    personalDays: [],
    universalMonths: [],
    universalWeeks: [9],
    masterNumbers: [],
    interpretation: 'Culminación liberadora; cierra vicios. En semanas 9, medita para fuerza espiritual.'
  },
  {
    id: 'disfrutar-arte',
    category: 'salud',
    name: 'Disfrutar del arte y la música',
    bestDay: 3,
    personalDays: [],
    universalMonths: [],
    universalWeeks: [],
    masterNumbers: [11],
    interpretation: 'Creatividad sanadora; eleva el espíritu. Combínalo con vibraciones 11 para inspiración.'
  },
  {
    id: 'ir-medico',
    category: 'salud',
    name: 'Ir al médico',
    bestDay: 7,
    personalDays: [],
    universalMonths: [7],
    universalWeeks: [],
    masterNumbers: [],
    interpretation: 'Análisis profundo; busca verdad interna. En meses 7, toma decisiones asertivas de salud.'
  },
  {
    id: 'iniciar-tratamiento',
    category: 'salud',
    name: 'Iniciar un tratamiento médico',
    bestDay: 4,
    personalDays: [],
    universalMonths: [],
    universalWeeks: [4],
    masterNumbers: [],
    interpretation: 'Estructura curativa; comprométete. Úsalo en semanas 4 para rutinas duraderas.'
  },
  {
    id: 'ir-terapia',
    category: 'salud',
    name: 'Ir a terapia',
    bestDay: 7,
    personalDays: [],
    universalMonths: [7],
    universalWeeks: [],
    masterNumbers: [],
    interpretation: 'Introspección emocional; sana heridas. Durante meses 7, conecta con tu sabiduría interior.'
  },
  {
    id: 'meditar-retiros',
    category: 'salud',
    name: 'Meditar o hacer retiros',
    bestDay: 7,
    personalDays: [],
    universalMonths: [],
    universalWeeks: [7],
    masterNumbers: [],
    interpretation: 'Espiritualidad pura; aísla para claridad. En semanas 7, alinea con objetivos anuales.'
  },
  {
    id: 'operaciones-cirugias',
    category: 'salud',
    name: 'Operaciones, cirugías, tratamientos',
    bestDay: 8,
    personalDays: [9],
    universalMonths: [],
    universalWeeks: [],
    masterNumbers: [],
    interpretation: 'Poder transformador; opera con fuerza kármica. Combínalo con días 9 para cierres.'
  },
  {
    id: 'cortar-pelo-unas',
    category: 'salud',
    name: 'Cortarte el pelo y las uñas',
    bestDay: 9,
    personalDays: [],
    universalMonths: [9],
    universalWeeks: [],
    masterNumbers: [],
    interpretation: 'Renovación simbólica; suelta lo viejo. En meses 9, prepara renacimientos.'
  },
  {
    id: 'cortar-mal-habito',
    category: 'salud',
    name: 'Cortar un mal hábito',
    bestDay: 9,
    personalDays: [],
    universalMonths: [],
    universalWeeks: [9],
    masterNumbers: [],
    interpretation: 'Cierre definitivo; libera karmas. Úsalo en semanas 9 para empoderamiento.'
  },

  // ═══════════════════════════════════════════════════════════════
  // ❤️ AMOR, FAMILIA Y PLACER
  // ═══════════════════════════════════════════════════════════════
  {
    id: 'iniciar-relacion',
    category: 'amor',
    name: 'Iniciar una relación amorosa',
    bestDay: 1,
    personalDays: [],
    universalMonths: [],
    universalWeeks: [1],
    masterNumbers: [],
    interpretation: 'Inicios apasionados; sé independiente en el amor. En semanas 1, atrae vínculos auténticos.'
  },
  {
    id: 'declarar-amor',
    category: 'amor',
    name: 'Declarar tu amor a alguien',
    bestDay: 3,
    personalDays: [],
    universalMonths: [3],
    universalWeeks: [],
    masterNumbers: [],
    interpretation: 'Expresión romántica; habla del corazón. Durante meses 3, nutre conexiones creativas.'
  },
  {
    id: 'fiesta-celebrar',
    category: 'amor',
    name: 'Hacer una fiesta o celebrar',
    bestDay: 3,
    personalDays: [],
    universalMonths: [],
    universalWeeks: [],
    masterNumbers: [],
    interpretation: 'Alegría social; celebra logros. Combínalo con vibraciones 5 para diversión liberadora.'
  },
  {
    id: 'reencontrar-queridos',
    category: 'amor',
    name: 'Reencontrarte con personas queridas',
    bestDay: 2,
    personalDays: [],
    universalMonths: [],
    universalWeeks: [2],
    masterNumbers: [],
    interpretation: 'Unión emocional; fortalece lazos. En semanas 2, prioriza equilibrio familiar.'
  },
  {
    id: 'visitar-familia',
    category: 'amor',
    name: 'Visitar a la familia',
    bestDay: 6,
    personalDays: [],
    universalMonths: [6],
    universalWeeks: [],
    masterNumbers: [],
    interpretation: 'Armonía hogareña; nutre raíces. Úsalo en meses 6 para decisiones de lealtad.'
  },
  {
    id: 'vacaciones',
    category: 'amor',
    name: 'Salir de vacaciones',
    bestDay: 5,
    personalDays: [],
    universalMonths: [],
    universalWeeks: [5],
    masterNumbers: [],
    interpretation: 'Aventura y libertad; desconecta. Durante semanas 5, recarga para objetivos anuales.'
  },
  {
    id: 'romance',
    category: 'amor',
    name: 'Romance',
    bestDay: 3,
    personalDays: [],
    universalMonths: [3],
    universalWeeks: [],
    masterNumbers: [],
    interpretation: 'Pasión creativa; enamórate con gozo. En meses 3, busca placer alineado.'
  },
  {
    id: 'recibir-casa',
    category: 'amor',
    name: 'Recibir en casa',
    bestDay: 6,
    personalDays: [2],
    universalMonths: [],
    universalWeeks: [],
    masterNumbers: [],
    interpretation: 'Hospitalidad nutricia; abre tu hogar. Combínalo con días 2 para armonía.'
  },
  {
    id: 'sexo-apasionado',
    category: 'amor',
    name: 'Sexo apasionado',
    bestDay: 5,
    personalDays: [],
    universalMonths: [],
    universalWeeks: [5],
    masterNumbers: [],
    interpretation: 'Intensidad liberadora; explora sentidos. Úsalo en semanas 5 para intimidad transformadora.'
  },
  {
    id: 'conocer-personas',
    category: 'amor',
    name: 'Conocer personas interesantes',
    bestDay: 5,
    personalDays: [],
    universalMonths: [5],
    universalWeeks: [],
    masterNumbers: [],
    interpretation: 'Encuentros inesperados; expande redes. Durante meses 5, atrae almas afines.'
  },
  {
    id: 'salir-ligue',
    category: 'amor',
    name: 'Salir de ligue',
    bestDay: 3,
    personalDays: [],
    universalMonths: [],
    universalWeeks: [3],
    masterNumbers: [],
    interpretation: 'Flirteo creativo; diviértete. En semanas 3, elige con intuición.'
  },
  {
    id: 'casarte',
    category: 'amor',
    name: 'Casarte',
    bestDay: 6,
    personalDays: [4],
    universalMonths: [],
    universalWeeks: [],
    masterNumbers: [],
    interpretation: 'Compromiso armónico; formaliza amor. Combínalo con vibraciones 4 para estabilidad.'
  },
  {
    id: 'deportes-extremos',
    category: 'amor',
    name: 'Divertirte con deportes extremos',
    bestDay: 5,
    personalDays: [],
    universalMonths: [5],
    universalWeeks: [],
    masterNumbers: [],
    interpretation: 'Adrenalina liberadora; desafía límites. Úsalo en meses 5.'
  },
  {
    id: 'divertirse-amigos',
    category: 'amor',
    name: 'Divertirte con amigos',
    bestDay: 3,
    personalDays: [],
    universalMonths: [],
    universalWeeks: [3],
    masterNumbers: [],
    interpretation: 'Socialización gozosa; ríe sin reservas. Durante semanas 3, nutre amistades.'
  },
  {
    id: 'solucionar-conflictos',
    category: 'amor',
    name: 'Solucionar conflictos familiares',
    bestDay: 2,
    personalDays: [],
    universalMonths: [2],
    universalWeeks: [],
    masterNumbers: [],
    interpretation: 'Diplomacia sanadora; restaura paz. En meses 2, toma decisiones asertivas.'
  },
  {
    id: 'juegos-mesa',
    category: 'amor',
    name: 'Jugar juegos de mesa',
    bestDay: 3,
    personalDays: [6],
    universalMonths: [],
    universalWeeks: [],
    masterNumbers: [],
    interpretation: 'Diversión creativa; conecta lúdicamente. Combínalo con días 6 para familia.'
  },
  {
    id: 'ir-bailar',
    category: 'amor',
    name: 'Ir a bailar',
    bestDay: 5,
    personalDays: [],
    universalMonths: [],
    universalWeeks: [5],
    masterNumbers: [],
    interpretation: 'Movimiento libre; libera tensiones. Úsalo en semanas 5 para placer renovador.'
  },
  {
    id: 'terminar-relacion',
    category: 'amor',
    name: 'Terminar una relación amorosa',
    bestDay: 9,
    personalDays: [],
    universalMonths: [9],
    universalWeeks: [],
    masterNumbers: [],
    interpretation: 'Cierre compasivo; suelta con gratitud. Durante meses 9, prepara renacimientos emocionales.'
  },
  {
    id: 'reunir-amigos',
    category: 'amor',
    name: 'Reunirte con tus amigos',
    bestDay: 3,
    personalDays: [],
    universalMonths: [],
    universalWeeks: [3],
    masterNumbers: [],
    interpretation: 'Alegría grupal; fortalece tribu. En semanas 3, alinea con metas sociales.'
  },
  {
    id: 'buscar-amigo-crisis',
    category: 'amor',
    name: 'Buscar a un amigo en crisis',
    bestDay: 6,
    personalDays: [],
    universalMonths: [],
    universalWeeks: [],
    masterNumbers: [],
    interpretation: 'Apoyo nutridor; sé el pilar. Combínalo con vibraciones 9 para sanación.'
  },
  {
    id: 'asistir-espectaculos',
    category: 'amor',
    name: 'Asistir a espectáculos',
    bestDay: 3,
    personalDays: [],
    universalMonths: [],
    universalWeeks: [3],
    masterNumbers: [],
    interpretation: 'Expresión cultural gozosa; conecta con inspiración creativa. En semanas 3, asiste para nutrir tu alma, priorizando eventos que alineen con tu liderazgo espiritual.'
  },
  {
    id: 'organizar-eventos-sociales',
    category: 'amor',
    name: 'Organizar o ir a eventos sociales',
    bestDay: 3,
    personalDays: [],
    universalMonths: [3],
    universalWeeks: [],
    masterNumbers: [],
    interpretation: 'Expresión social creativa; networking con alegría carismática. En meses 3, planea con pasión, priorizando contactos que eleven tu liderazgo y abundancia.'
  },

  // ═══════════════════════════════════════════════════════════════
  // 🏠 VIDA DIARIA
  // ═══════════════════════════════════════════════════════════════
  {
    id: 'remodelaciones',
    category: 'vida',
    name: 'Hacer remodelaciones o reparaciones',
    bestDay: 4,
    personalDays: [],
    universalMonths: [],
    universalWeeks: [4],
    masterNumbers: [],
    interpretation: 'Estructura hogareña; renueva bases. En semanas 4, cierra ciclos de desorden.'
  },
  {
    id: 'trabajos-manuales',
    category: 'vida',
    name: 'Realizar trabajos manuales',
    bestDay: 4,
    personalDays: [],
    universalMonths: [4],
    universalWeeks: [],
    masterNumbers: [],
    interpretation: 'Trabajo tangible; crea con manos. Durante meses 4, enfócate en estabilidad diaria.'
  },
  {
    id: 'limpiar-casa',
    category: 'vida',
    name: 'Limpiar la casa',
    bestDay: 4,
    personalDays: [],
    universalMonths: [],
    universalWeeks: [4],
    masterNumbers: [],
    interpretation: 'Purificación estructurada; ordena energías. Úsalo en semanas 4 para claridad mental.'
  },
  {
    id: 'cocinar',
    category: 'vida',
    name: 'Cocinar delicioso',
    bestDay: 6,
    personalDays: [3],
    universalMonths: [],
    universalWeeks: [],
    masterNumbers: [],
    interpretation: 'Nutrición amorosa; deleita sentidos. Combínalo con días 3 para creatividad culinaria.'
  },
  {
    id: 'sembrar-plantas',
    category: 'vida',
    name: 'Sembrar, podar plantas para crecer',
    bestDay: 6,
    personalDays: [],
    universalMonths: [6],
    universalWeeks: [],
    masterNumbers: [],
    interpretation: 'Fertilidad y crecimiento; planta intenciones. En meses 6, nutre proyectos vitales.'
  },
  {
    id: 'estudiar-examen',
    category: 'vida',
    name: 'Estudiar o prepararte para un examen',
    bestDay: 7,
    personalDays: [],
    universalMonths: [],
    universalWeeks: [7],
    masterNumbers: [],
    interpretation: 'Concentración introspectiva; absorbe conocimiento. Durante semanas 7, alinea con sabiduría.'
  },
  {
    id: 'salir-compras',
    category: 'vida',
    name: 'Salir de compras',
    bestDay: 3,
    personalDays: [],
    universalMonths: [3],
    universalWeeks: [],
    masterNumbers: [],
    interpretation: 'Compras creativas; elige con gozo. Úsalo en meses 3 para adquisiciones alegres.'
  },
  {
    id: 'iniciar-cursos',
    category: 'vida',
    name: 'Iniciar cursos o diplomados',
    bestDay: 1,
    personalDays: [],
    universalMonths: [],
    universalWeeks: [1],
    masterNumbers: [],
    interpretation: 'Inicios educativos; lidera tu aprendizaje. En semanas 1, invierte en crecimiento.'
  },
  {
    id: 'retomar-estudios',
    category: 'vida',
    name: 'Retomar estudios inconclusos',
    bestDay: 9,
    personalDays: [7],
    universalMonths: [],
    universalWeeks: [],
    masterNumbers: [],
    interpretation: 'Culminación académica; cierra pendientes. Combínalo con vibraciones 7 para profundidad.'
  },
  {
    id: 'dia-mudanza',
    category: 'vida',
    name: 'Día de mudanza',
    bestDay: 5,
    personalDays: [],
    universalMonths: [5],
    universalWeeks: [],
    masterNumbers: [],
    interpretation: 'Cambio residencial; abraza lo nuevo. Durante meses 5, transita con libertad.'
  },
  {
    id: 'nuevo-lugar',
    category: 'vida',
    name: 'Empezar a vivir en un nuevo lugar',
    bestDay: 1,
    personalDays: [],
    universalMonths: [],
    universalWeeks: [1],
    masterNumbers: [],
    interpretation: 'Nuevo capítulo; independízate. Úsalo en semanas 1 para inicios potentes.'
  },
  {
    id: 'actividades-artisticas',
    category: 'vida',
    name: 'Actividades artísticas como pintar',
    bestDay: 3,
    personalDays: [],
    universalMonths: [3],
    universalWeeks: [],
    masterNumbers: [],
    interpretation: 'Expresión creativa; fluye con alma. En meses 3, nutre tu esencia artística.'
  },
  {
    id: 'asumir-compromiso',
    category: 'vida',
    name: 'Asumir un compromiso',
    bestDay: 6,
    personalDays: [4],
    universalMonths: [],
    universalWeeks: [],
    masterNumbers: [],
    interpretation: 'Responsabilidad armónica; comprométete. Combínalo con días 4 para solidez.'
  },
  {
    id: 'comprar-importante',
    category: 'vida',
    name: 'Comprar algo importante',
    bestDay: 8,
    personalDays: [],
    universalMonths: [],
    universalWeeks: [8],
    masterNumbers: [],
    interpretation: 'Decisión poderosa; elige con ambición. Durante semanas 8, atrae calidad.'
  },
  {
    id: 'comprar-auto',
    category: 'vida',
    name: 'Comprar un automóvil',
    bestDay: 5,
    personalDays: [],
    universalMonths: [5],
    universalWeeks: [],
    masterNumbers: [],
    interpretation: 'Movilidad liberadora; invierte en aventura. Úsalo en meses 5 para cambios prácticos.'
  },
  {
    id: 'quedarse-casa',
    category: 'vida',
    name: 'Quedarte en casa y descansar',
    bestDay: 7,
    personalDays: [],
    universalMonths: [],
    universalWeeks: [7],
    masterNumbers: [],
    interpretation: 'Retiro introspectivo; recarga solo. En semanas 7, medita para decisiones futuras.'
  },
  {
    id: 'iniciar-actividades',
    category: 'vida',
    name: 'Iniciar actividades nuevas',
    bestDay: 1,
    personalDays: [],
    universalMonths: [],
    universalWeeks: [],
    masterNumbers: [],
    interpretation: 'Audacia inicial; rompe rutinas. Combínalo con vibraciones 5 para dinamismo.'
  },
  {
    id: 'arreglar-auto',
    category: 'vida',
    name: 'Arreglar tu auto',
    bestDay: 4,
    personalDays: [],
    universalMonths: [],
    universalWeeks: [4],
    masterNumbers: [],
    interpretation: 'Estructura práctica y solidez; repara con disciplina para estabilidad. En semanas 4 para mantenimientos que simbolicen bases firmes, recomendando orden en tu movilidad.'
  },
  {
    id: 'fumigar-desinfectar',
    category: 'vida',
    name: 'Fumigar, desinfectar',
    bestDay: 4,
    personalDays: [],
    universalMonths: [4],
    universalWeeks: [],
    masterNumbers: [],
    interpretation: 'Limpieza estructurada; purifica con orden práctico. Durante meses 4, detox espacios, aconsejando disciplina para bases sólidas libres de energías estancadas.'
  },
  {
    id: 'ir-al-campo',
    category: 'vida',
    name: 'Ir al campo',
    bestDay: 6,
    personalDays: [],
    universalMonths: [6],
    universalWeeks: [],
    masterNumbers: [],
    interpretation: 'Conexión nutricia con naturaleza; relaja con armonía terrenal. Úsalo en meses 6 para escapadas, recomendando gratitud para equilibrio vital que apoye decisiones cotidianas.'
  }
];

/**
 * Obtiene las actividades que coinciden con los números del día
 * @param {number} universalDay - Día Universal (1-9, 11, 22)
 * @param {number} personalDay - Día Personal del usuario (1-9, 11, 22)
 * @param {number} universalMonth - Mes Universal (1-9, 11, 22)
 * @param {number} universalWeek - Semana Universal (1-9)
 * @returns {Object} Actividades organizadas por tipo de match
 */
export function getMatchingActivities(universalDay, personalDay, universalMonth, universalWeek) {
  const tripleMatch = [];
  const doubleMatch = [];
  const singleMatch = [];

  ACTIVITIES.forEach(activity => {
    let matchCount = 0;
    const matchTypes = [];

    // Match por Día Universal (principal)
    if (activity.bestDay === universalDay) {
      matchCount++;
      matchTypes.push('día');
    }

    // Match por Día Personal mencionado en la actividad
    if (activity.personalDays.includes(personalDay)) {
      matchCount++;
      matchTypes.push('personal');
    }

    // Match por Mes Universal mencionado
    if (activity.universalMonths.includes(universalMonth)) {
      matchCount++;
      matchTypes.push('mes');
    }

    // Match por Semana Universal mencionada
    if (activity.universalWeeks.includes(universalWeek)) {
      matchCount++;
      matchTypes.push('semana');
    }

    // Match por Números Maestros (si el día universal es 11 o 22)
    if (activity.masterNumbers.includes(universalDay) || 
        activity.masterNumbers.includes(personalDay)) {
      matchCount++;
      matchTypes.push('maestro');
    }

    // Categorizar según cantidad de matches
    if (matchCount >= 3) {
      tripleMatch.push({ ...activity, matchTypes, matchCount });
    } else if (matchCount === 2) {
      doubleMatch.push({ ...activity, matchTypes, matchCount });
    } else if (matchCount === 1) {
      singleMatch.push({ ...activity, matchTypes, matchCount });
    }
  });

  // Ordenar por matchCount descendente
  const sortByMatch = (a, b) => b.matchCount - a.matchCount;

  return {
    tripleMatch: tripleMatch.sort(sortByMatch),
    doubleMatch: doubleMatch.sort(sortByMatch),
    singleMatch: singleMatch.sort(sortByMatch),
    total: tripleMatch.length + doubleMatch.length + singleMatch.length
  };
}

/**
 * Obtiene la categoría por su ID
 */
export function getCategoryById(categoryId) {
  return ACTIVITY_CATEGORIES.find(cat => cat.id === categoryId);
}

/**
 * Agrupa actividades por categoría
 */
export function groupActivitiesByCategory(activities) {
  const grouped = {};
  
  ACTIVITY_CATEGORIES.forEach(cat => {
    grouped[cat.id] = {
      ...cat,
      activities: activities.filter(act => act.category === cat.id)
    };
  });

  return grouped;
}

/**
 * Busca actividades por texto
 */
export function searchActivities(query) {
  if (!query || query.length < 2) return [];
  
  const normalizedQuery = query.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  
  return ACTIVITIES.filter(activity => {
    const normalizedName = activity.name.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    return normalizedName.includes(normalizedQuery);
  });
}

